/* eslint-disable react/forbid-dom-props */
import type { AnchorHTMLAttributes } from 'react';
import React, { useRef, useState } from 'react';

import classNames from 'classnames';

import { useTruncate } from '@travauxlib/shared/src/hooks/useTruncate';

import { ActionsColumn } from './ActionsColumn';
import { AdditionalRowContent } from './AdditionalRowContent';
import { ExpandColumn } from './ExpandColumn';
import {
  alignColumnClassName,
  columnClassName,
  makeCommonRowClassName,
  sizePaddingColumnClassName,
  verticalFirstColShadowClassName,
  verticalLastColShadowClassName,
} from './utils';

import { GenericLink } from '../Links';

import type { ColumnConfig, RowAction, SizePaddingClassName } from './types';
import type { ActionListProps } from '../ActionList';
import type { Placement } from '@floating-ui/react';

type Props<T> = {
  item: T;
  rowIndex: number;
  onClickExpand: () => void;
  isExpanded: boolean;
  columnConfigs: ColumnConfig<T>[];
  rowActions?: (item: T) => RowAction[] | React.ReactElement;
  actionListProps?: Partial<ActionListProps<T>>;
  additionalRowContent?: React.ReactElement;
  renderAdditionalRowContent?: (line: T) => React.ReactElement;
  fixedColumn?: 'first' | 'last';
  hasHorizontalScroll: boolean;
  hasExpandableRows: boolean;
  onRowClick?: (item: T) => {
    action?: () => void;
    to?: string;
    href?: AnchorHTMLAttributes<T>['href'];
    target?: AnchorHTMLAttributes<T>['target'];
  };
  disableRow?: (item: T) => boolean;
  wrapLines?: boolean;
  inline?: boolean;
  size: SizePaddingClassName;
  rowActionsPosition?: Placement;
};

export const TableRow = <T extends object>({
  item,
  rowIndex,
  size,
  columnConfigs,
  rowActions,
  additionalRowContent,
  onClickExpand,
  isExpanded,
  fixedColumn,
  hasHorizontalScroll,
  hasExpandableRows,
  onRowClick,
  renderAdditionalRowContent,
  wrapLines,
  inline,
  disableRow,
  rowActionsPosition,
  actionListProps,
}: Props<T>): React.ReactElement => {
  const truncateWordsWithTooltip = useTruncate();
  const [rowHeight, setRowHeight] = useState();
  const isDisabled = !!disableRow?.(item);
  const columnCount = columnConfigs.length + (rowActions ? 1 : 0) + (hasExpandableRows ? 1 : 0);
  const rowRef = useRef(null);
  const isRowALink = onRowClick?.(item).to || onRowClick?.(item).href;

  React.useEffect(() => {
    setRowHeight((rowRef?.current as any)?.getBoundingClientRect().height);
  }, [rowRef]);

  const handleRowClick = (isRowClickDisabled: boolean): void => {
    if (isRowClickDisabled) {
      return;
    }

    const action = onRowClick?.(item)?.action;

    if (action) {
      action();
    } else if (additionalRowContent || renderAdditionalRowContent) {
      onClickExpand();
    }
  };

  const actions = rowActions?.(item);
  const isRowActionsAnArray = Array.isArray(actions);
  return (
    <tbody
      className={classNames('group [&:hover>tr]:bg-primarylight ctn-neutral-white', {
        'text-neutral-default [&:hover>tr]:bg-transparent': isDisabled,
      })}
      ref={rowRef}
    >
      <tr className={classNames('bg-inherit', { relative: isRowALink })}>
        {columnConfigs.map((config, index) => {
          const colData = config.columnKey && item[config.columnKey];

          const colSubText = config.subTextKey && item[config.subTextKey];

          const cellContent = (
            <>
              {colSubText && <div className="text-secondary text-sm">{colSubText.toString()}</div>}
              {config?.renderValue?.(colData, item, rowIndex) ||
                truncateWordsWithTooltip({
                  // If we don't have a renderValue, colData will be either a string or a number
                  // We use unknown to translate type from T[keyof T] to string
                  value: colData as unknown as string,
                  maxCharsForScreenSize: config.maxCharsForScreenSize,
                })}
            </>
          );

          return (
            <td
              className={classNames(
                sizePaddingColumnClassName[size],
                makeCommonRowClassName(
                  !!(
                    (onRowClick || additionalRowContent || renderAdditionalRowContent) &&
                    !isDisabled &&
                    !config.isRowClickDisabled
                  ),
                ),
                columnClassName,
                {
                  [verticalFirstColShadowClassName]: fixedColumn === 'first' && hasHorizontalScroll,
                  [verticalLastColShadowClassName]:
                    fixedColumn === 'last' && hasHorizontalScroll && !rowActions?.length,
                  'whitespace-normal!': wrapLines,
                  'group-first:border-t-0': inline,
                },
                alignColumnClassName(config.align),
                config.className,
              )}
              key={`col-${String(config.columnKey)}-${index}`}
              onClick={() => handleRowClick(!!config.isRowClickDisabled)}
            >
              {!isDisabled && !config.isRowClickDisabled && isRowALink ? (
                <GenericLink
                  className="after:absolute after:inset-0 block text-inherit no-underline"
                  to={onRowClick(item).to}
                  href={onRowClick(item).href}
                  target={onRowClick(item).target}
                >
                  {cellContent}
                </GenericLink>
              ) : (
                cellContent
              )}
            </td>
          );
        })}
        {actions && isRowActionsAnArray && actions.length !== 0 && (
          <ActionsColumn
            height={rowHeight}
            rowActions={actions}
            fixedColumn={fixedColumn}
            rowActionsPosition={rowActionsPosition}
            actionListProps={actionListProps}
          />
        )}
        {actions && !isRowActionsAnArray && (
          <>
            {fixedColumn === 'last' && (
              <td className={classNames(columnClassName, 'invisible')}>{actions}</td>
            )}
            <td
              className={classNames(
                columnClassName,
                fixedColumn === 'last' && verticalLastColShadowClassName,
              )}
              style={{ height: `${rowHeight}px` }}
            >
              {actions}
            </td>
          </>
        )}
        {hasExpandableRows && (
          <ExpandColumn
            size={size}
            hasAdditionalContent={!!(additionalRowContent || renderAdditionalRowContent)}
            onClickExpand={onClickExpand}
            isExpanded={isExpanded}
          />
        )}
      </tr>
      {isExpanded && (
        <AdditionalRowContent
          additionalRowContent={additionalRowContent || renderAdditionalRowContent?.(item)}
          isRowClickable={!!(onRowClick || additionalRowContent)}
          onRowClick={() => handleRowClick(false)}
          columnCount={columnCount}
          size={size}
        />
      )}
    </tbody>
  );
};
